import React, { Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import classnames from "classnames";
import {
  getPriceTagging,
  formatMoney,
  getIndividualPriceTagging,
  isPlanMemberGrouped,
  isGroupPlan,
  isPlanRequireUW,
} from "./EnrollmentDataUtil";
import messages from "components/messagesDescription";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl";
import IntervalOptions from "./PlanIntervalOptionsPP";
import UwIcon from "../../../assets/images/svg/uw_icon.svg";
import { changePlan } from "../../../actions/enrollmentActions";
import { SUM_ASSURED_BASED } from "./constants";

const PlanBoxPP = ({ benefit, plan, intl }) => {
  const dispatch = useDispatch();
  const priceTagMode = useSelector(state => state.enrollment.details.priceTagMode);
  const showIntervalOptions =
    Array.isArray(plan.intervalOptions) && plan.intervalOptions.length > 0;
  const isPlanGrouped = plan.groupID && Array.isArray(plan.plans);
  const isDefaultPlan =
    benefit.benefitOptions.defaultOption &&
    ((!isPlanGrouped && plan.id === benefit.benefitOptions.defaultOption.id) ||
      (isPlanGrouped && plan.plans.some(p => p.id == benefit.benefitOptions.defaultOption.id)));
  const isRecommendedPlan =
    benefit.benefitOptions.recommendedOption &&
    plan.id === benefit.benefitOptions.recommendedOption.id;

  const componentCls = classnames(
    "product-plan",
    "new-product-plan",
    "button--tiles",
    "button--tiles--selectable",
    {
      "button--tiles--active": plan.isSelected,
      "button--tiles--disabled": !plan.isAvailable,
    }
  );
  const isMemberGroup = isPlanMemberGrouped(plan);
  const isPlanBased = isGroupPlan(plan);
  const PlanName = () => {
    const description = isPlanBased ? plan.plans[0].description : plan.description;
    const planName = isPlanBased ? plan.plans[0].planName : plan.planName;
    const planDescription = description || planName;
    return planDescription && isPlanBased ? (
      <div className="product-plan__name" title={planDescription} style={{ fontSize: "1rem" }}>
        <span>
          <strong>
            {planDescription &&
              intl
                .formatMessage({
                  id: planDescription,
                  defaultMessage: planDescription,
                })
                .replace(/\//gi, " /")}
          </strong>
        </span>
      </div>
    ) : (
      ""
    );
  };

  const PlanDescription = () => {
    const description = plan.descriptionLine1;
    return description ? (
      <div className="product-plan__name" title={description} style={{ fontSize: "1rem" }}>
        <span>
          <strong>
            {description &&
              intl
                .formatMessage({
                  id: description,
                  defaultMessage: description,
                })
                .replace(/\//gi, " /")}
          </strong>
        </span>
      </div>
    ) : (
      ""
    );
  };

  const PlanSubDescription = () => {
    const subDescription =
      plan.descriptionLine2 || (isPlanBased && plan.groupValue != "0" && plan.plans[0].groupName);
    return subDescription ? (
      <div className="product-plan__name" title={subDescription} style={{ fontSize: "1rem" }}>
        <span>
          <strong>
            {subDescription &&
              intl
                .formatMessage({
                  id: subDescription,
                  defaultMessage: subDescription,
                })
                .replace(/\//gi, " /")}
          </strong>
        </span>
      </div>
    ) : (
      ""
    );
  };

  const PlanBadge = () => {
    return isDefaultPlan && isRecommendedPlan ? (
      <div className="product-plan__badge">
        <FormattedMessage {...messages.enrolment.defaultAndPopularDescription} />
      </div>
    ) : isDefaultPlan ? (
      <div className="product-plan__badge">
        <FormattedMessage {...messages.defaultDescription} />
      </div>
    ) : isRecommendedPlan ? (
      <div className="product-plan__badge">
        <FormattedMessage {...messages.enrolment.popularDescription} />
      </div>
    ) : (
      <div className="product-plan__badge" aria-hidden="true"></div>
    );
  };

  const PlanValue = () => {
    const planValue = plan.planValue && plan.planValue.numericValue;
    return planValue ? (
      <span className="product-plan__description">
        <span>
          {benefit.type === SUM_ASSURED_BASED &&
            intl.formatMessage({ id: "Sum Assured", defaultMessage: "Sum Assured" })}
          <br />
          <strong>{formatMoney(planValue)}</strong>
        </span>
      </span>
    ) : (
      ""
    );
  };

  const getPlanPrice = plan => {
    const eligibleMembers =
      plan.eligibleMembers || (plan.plans[0] && plan.plans[0].eligibleMembers);
    return eligibleMembers.selectionMode === "Individual"
      ? getIndividualPriceTagging(
          priceTagMode,
          eligibleMembers.members,
          benefit.benefitOptions.planType
        )
      : getPriceTagging(priceTagMode, eligibleMembers.members, plan);
  };

  const PlanPrice = () => {
    const pricing = getPlanPrice(plan);
    const priceMode = !pricing.isRelativePrice
      ? "absolute"
      : pricing.relativeTagNode === "Standard"
      ? "standard"
      : "relative";

    return (
      <div className="plan-price">
        {priceMode === "absolute" ? (
          <div>
            {pricing.price != 0 &&
              (pricing.price > 0 ? (
                <FormattedMessage {...messages.enrollment.spendDescription} />
              ) : (
                <FormattedMessage {...messages.enrollment.creditDescription} />
              ))}
            &nbsp;
            <span>
              <strong>{formatMoney(Math.abs(pricing.price))}</strong>
            </span>
          </div>
        ) : priceMode === "relative" ? (
          <div>
            {pricing.price != 0 &&
              (pricing.relativePrice > 0 ? (
                <FormattedMessage {...messages.enrollment.spendDescription} />
              ) : (
                <FormattedMessage {...messages.enrollment.creditDescription} />
              ))}
            &nbsp;
            <strong>{formatMoney(Math.abs(pricing.relativePrice))}</strong>
          </div>
        ) : (
          <FormattedMessage {...messages.standardDescription} />
        )}
      </div>
    );
  };

  const IntervalPlanPrice = () => {
    const price = plan.pricing.price;
    return (
      <div className="plan-price">
        <div>
          <FormattedMessage {...messages.enrollment.spendDescription} />
          &nbsp;
          <span>
            <strong>{formatMoney(price.numericValue || 0)}</strong>
          </span>
        </div>
      </div>
    );
  };

  const HDFNote = () => {
    const isHDFRequired = isPlanRequireUW(plan);
    return (
      <span className="button--tiles__note">
        {isHDFRequired && (
          <Fragment>
            <span className="product-plan__hdfDescription">
              <FormattedHTMLMessage {...messages.HDFRDescription} />
            </span>
            <span className="product-plan__hdfIcon">
              <UwIcon />
            </span>
          </Fragment>
        )}
      </span>
    );
  };

  return (
    <div className="grid__item grid__item--three product-panel__plan">
      <div className="product-plan-wrapper">
        <div className="product-plan__badges">
          <PlanBadge />
        </div>
        {showIntervalOptions && (
          <IntervalOptions plan={plan} benefitId={benefit.benefitID} disabled={!plan.isAvailable} intl={intl} />
        )}
        <button
          type="button"
          style={{ textAlign: "center" }}
          onClick={() => dispatch(changePlan(benefit, plan))}
          className={componentCls}
          disabled={!plan.isAvailable}
        >
          <div className="product-plan__details">
            {showIntervalOptions ? (
              <Fragment>
                <PlanValue />
                <IntervalPlanPrice />
              </Fragment>
            ) : (
              <Fragment>
                <PlanName />
                <PlanDescription />
                <PlanSubDescription />
                {plan.name !== "No Coverage" && <PlanValue />}
                {!isMemberGroup && <PlanPrice />}
              </Fragment>
            )}
            <HDFNote />
          </div>
        </button>
      </div>
    </div>
  );
};

PlanBoxPP.propTypes = {
  benefit: PropTypes.object.isRequired,
  plan: PropTypes.object.isRequired,
  isNarrowScreen: PropTypes.bool,
  intl: PropTypes.func,
};
export default PlanBoxPP;



// WEBPACK FOOTER //
// ./components/employee/shop/PlanBoxPP.js